/** Projects (phase 3): what a reader's project role shows them, and the
 *  figures a project's overview counts. The server decides what each role
 *  may do (services/projects.py); this only hides what it would refuse. */
import type { Project, ProjectRole, Station } from '../api/types'
import { systemStatus } from './freshness'
import { searchTerms } from './stationSearch'

const RANK: Record<ProjectRole, number> = { viewer: 1, editor: 2, admin: 3 }

/** Editors and Admins change a project's details and stations. */
export function canEdit(role: ProjectRole): boolean {
  return RANK[role] >= RANK.editor
}

/** Only a project's Admins change its people. */
export function canManagePeople(role: ProjectRole): boolean {
  return role === 'admin'
}

/** Only a project's Admins archive it. */
export function canArchive(role: ProjectRole): boolean {
  return role === 'admin'
}

export function roleLabel(role: ProjectRole): string {
  return { viewer: 'Viewer', editor: 'Editor', admin: 'Admin' }[role]
}

/** Every word of the query in the project's name or client, in any order,
 *  matched as the station search matches. */
export function filterProjects(projects: Project[], query: string): Project[] {
  const terms = searchTerms(query)
  if (!terms.length) return projects
  return projects.filter((p) => {
    const text = searchTerms(`${p.name} ${p.client}`).join(' ')
    return terms.every((term) => text.includes(term))
  })
}

export interface ProjectTiles { stations: number; receiving: number; noData: number; notChecked: number }

/** The overview's tiles, by the system status Home shows for each station
 *  (lib/freshness.ts `systemStatus`), so a project and Home never disagree. */
export function projectTiles(stations: Station[], checksStale: boolean): ProjectTiles {
  const counts = { stations: stations.length, receiving: 0, noData: 0, notChecked: 0 }
  for (const station of stations) {
    const { status } = systemStatus(station, checksStale)
    if (status === 'receiving') counts.receiving += 1
    else if (status === 'no_data') counts.noData += 1
    else counts.notChecked += 1
  }
  return counts
}

/** Home's "No project" choice: stations in none of the reader's projects. */
export const NO_PROJECT = '\u0000none'

/** Home's project filter: the stations in the named project (the reader's
 *  `Station.projects`), or in none of the reader's projects, or all of them
 *  for no filter. The same station objects come back, so a station's status
 *  and freshness never change with the filter; only the counts do. */
export function byProject(stations: Station[], project: string | null): Station[] {
  if (project === null) return stations
  if (project === NO_PROJECT) return stations.filter((s) => !(s.projects ?? []).length)
  return stations.filter((s) => (s.projects ?? []).includes(project))
}

/** The No data stations a project filter hides, by name: Home names them in
 *  its notice so a silent station is never out of sight (the board's guard). */
export function hiddenNoData(all: Station[], shown: Station[],
                             noData: (station: Station) => boolean): string[] {
  const visible = new Set(shown.map((s) => s.code))
  return all.filter((s) => !visible.has(s.code) && noData(s)).map((s) => s.name)
}

/** A project card's status line, as Home would put it: its No data
 *  stations by name, except that once the health checks have stopped a
 *  station only the monitor's silence made No data reads Monitoring delayed, as do
 *  the rest (lib/freshness systemStatus). */
export function cardStatus(project: Project, checksStale: boolean): { noData: string[]; notChecked: boolean } {
  const noData = project.no_data_stations
    .filter((s) => !(checksStale && s.reason === 'silent')).map((s) => s.name)
  return { noData, notChecked: checksStale && project.station_count > noData.length }
}

/** Home's `?project=`, read against the reader's projects. Until the list
 *  arrives, or if it fails, or the project is not one of theirs, nothing is
 *  filtered and `projectFilterNotice` says so; the address keeps the
 *  parameter, so a search typed meanwhile does not drop it. */
export type ProjectFilter =
  | { kind: 'all' | 'pending' | 'unknown'; name: null }
  | { kind: 'project' | 'none'; name: string }
  | { kind: 'failed'; name: null; error: string }

export function projectFilter(param: string | null, projects: Project[] | undefined,
                              error?: string): ProjectFilter {
  if (!param) return { kind: 'all', name: null }
  if (param === 'none') return { kind: 'none', name: NO_PROJECT }
  if (!projects) return error ? { kind: 'failed', name: null, error } : { kind: 'pending', name: null }
  const chosen = projects.find((p) => String(p.id) === param)
  return chosen ? { kind: 'project', name: chosen.name } : { kind: 'unknown', name: null }
}

export function projectFilterNotice(filter: ProjectFilter): string | null {
  switch (filter.kind) {
    case 'pending': return 'Loading the project filter…'
    case 'failed': return `The project filter could not be loaded (${filter.error}), so every station is shown.`
    case 'unknown': return 'That project is not one of yours, or it has been archived, so every station is shown.'
    default: return null
  }
}

/** An Explore path that keeps the project the page was opened from (its
 *  `?project=`), so moving between a gateway station's sensors does not
 *  drop the project and with it the export's project name. */
export function withProject(path: string, projectParam: string | null): string {
  return projectParam ? `${path}?project=${encodeURIComponent(projectParam)}` : path
}

export const PROJECT_TABS = ['overview', 'chat', 'logs', 'images', 'documents', 'sensor-docs'] as const
export type ProjectTab = typeof PROJECT_TABS[number]

/** The project view's tab, from the address (`?tab=`), so a link opens it. */
export function tabFrom(params: URLSearchParams): ProjectTab {
  const asked = params.get('tab')
  return (PROJECT_TABS as readonly string[]).includes(asked ?? '') ? asked as ProjectTab : 'overview'
}
